import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, Plus } from "lucide-react";
import api from "../lib/api";
import Header from "../components/dashboard/Header";
import { InstagramConnect } from "../components/social/InstagramConnect";
import { UploadZone, PhotoGrid } from "../components/social/SocialLibrary";
import { PostComposer } from "../components/social/PostComposer";
import { SocialCalendar } from "../components/social/SocialCalendar";
import { SocialPerformance, BestTimesCard } from "../components/social/SocialPerformance";

const TabBtn = ({ on, children, ...p }) => (
  <button {...p} className={`px-4 py-2 rounded-full text-sm transition-colors ${on ? "bg-brand-ink text-brand-page" : "text-brand-muted hover:text-brand-ink"}`}>{children}</button>
);

export default function Social() {
  const [tab, setTab] = useState("planner");
  const [photos, setPhotos] = useState([]);
  const [tags, setTags] = useState([]);
  const [posts, setPosts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState({ location: "all", tag: "all", unused: false });
  const [selected, setSelected] = useState([]);
  const [compose, setCompose] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [planning, setPlanning] = useState(false);

  const loadPhotos = useCallback(() => api.get("/social/photos", { params: filter }).then(({ data }) => { setPhotos(data.photos); setTags(data.tags); }), [filter]);
  const loadPosts = useCallback(() => api.get("/social/posts").then(({ data }) => setPosts(data.posts)), []);
  useEffect(() => { loadPhotos(); }, [loadPhotos]);
  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => { api.get("/bookings/config").then(({ data }) => setLocations((data.mappings || []).map((m) => m.location))).catch(() => {}); }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 10 ? [...s, id] : s));
  const remove = async (id) => { try { await api.delete(`/social/photos/${id}`); loadPhotos(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); } };
  const plan = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post("/social/ai/plan", { days: 14, per_week: 3, tz_offset_minutes: -new Date().getTimezoneOffset() });
      toast.success(`Drafted ${data.posts.length} posts for the next 2 weeks — review and schedule them`);
      loadPosts(); loadPhotos(); setTab("planner");
    } catch (e) { toast.error(e?.response?.data?.detail || "Planning failed"); }
    finally { setPlanning(false); }
  };
  const saved = () => { setCompose(null); setSelected([]); loadPosts(); loadPhotos(); };
  const drafts = posts.filter((p) => p.status === "draft");
  const counts = posts.reduce((a, p) => ({ ...a, [p.status]: (a[p.status] || 0) + 1 }), {});

  return (
    <div className="min-h-screen bg-brand-page">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-tertiary mb-2">Sales &amp; Growth</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight" data-testid="social-title">Instagram</h1>
            <p className="text-sm text-brand-muted mt-2">{counts.scheduled || 0} scheduled · {counts.draft || 0} drafts · {counts.published || 0} posted{counts.failed ? ` · ${counts.failed} failed` : ""}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
            <InstagramConnect />
            <button onClick={plan} disabled={planning} data-testid="social-plan-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-60">
              {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {planning ? "Planning…" : "Fill my next 2 weeks"}
            </button>
          </div>
        </div>

        <div className="flex gap-1 rounded-full border border-brand-border bg-brand-surface p-1 w-fit">
          <TabBtn on={tab === "planner"} onClick={() => setTab("planner")} data-testid="social-tab-planner">Planner</TabBtn>
          <TabBtn on={tab === "library"} onClick={() => setTab("library")} data-testid="social-tab-library">Photo library ({photos.length})</TabBtn>
          <TabBtn on={tab === "performance"} onClick={() => setTab("performance")} data-testid="social-tab-performance">Performance</TabBtn>
        </div>

        {tab === "planner" && (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
            <SocialCalendar month={month} setMonth={setMonth} posts={posts} onOpen={(p) => setCompose({ post: p })} />
            <div className="space-y-4">
            <BestTimesCard />
            <div className="rounded-xl border border-brand-border bg-brand-surface p-5 h-fit" data-testid="social-drafts">
              <h3 className="font-serif text-xl text-brand-ink mb-1">Drafts to review</h3>
              <p className="text-xs text-brand-muted mb-4">Open a draft, tweak the caption, then Schedule.</p>
              <div className="space-y-2 max-h-[560px] overflow-y-auto">
                {drafts.map((p) => (
                  <button key={p.id} onClick={() => setCompose({ post: p })} data-testid={`draft-${p.id}`} className="w-full flex gap-3 rounded-lg p-2 hover:bg-brand-hover text-left">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/social/${p.photo_ids[0]}.thumb.jpg`} alt="" className="h-14 w-12 rounded object-cover" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-brand-tertiary">{p.scheduled_at ? new Date(p.scheduled_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "No date"} · {p.location || "Brand"}</span>
                      <span className="block text-xs text-brand-ink line-clamp-2">{p.caption || "No caption yet"}</span>
                    </span>
                  </button>
                ))}
                {!drafts.length && <p className="text-sm text-brand-tertiary">No drafts. Try "Fill my next 2 weeks".</p>}
              </div>
            </div>
            </div>
          </div>
        )}

        {tab === "performance" && <SocialPerformance />}

        {tab === "library" && (
          <div className="space-y-5">
            <UploadZone locations={locations} tags={tags} onDone={loadPhotos} />
            <div className="flex flex-wrap items-center gap-3">
              <select value={filter.location} onChange={(e) => setFilter({ ...filter, location: e.target.value })} data-testid="social-filter-location" className="text-sm rounded-lg border border-brand-border bg-brand-surface px-3 py-2">
                <option value="all">All locations</option>{locations.map((l) => <option key={l}>{l}</option>)}
              </select>
              <select value={filter.tag} onChange={(e) => setFilter({ ...filter, tag: e.target.value })} data-testid="social-filter-tag" className="text-sm rounded-lg border border-brand-border bg-brand-surface px-3 py-2">
                <option value="all">All themes</option>{tags.map((t) => <option key={t}>{t}</option>)}
              </select>
              <label className="text-sm text-brand-muted flex items-center gap-2"><input type="checkbox" checked={filter.unused} onChange={(e) => setFilter({ ...filter, unused: e.target.checked })} data-testid="social-filter-unused" /> Never posted</label>
              {selected.length > 0 && (
                <button onClick={() => setCompose({ photoIds: selected })} data-testid="social-create-post" className="ml-auto inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-page px-4 py-2 text-sm">
                  <Plus className="h-4 w-4" /> Create post with {selected.length} photo{selected.length > 1 ? "s" : ""}
                </button>
              )}
            </div>
            <PhotoGrid photos={photos} selected={selected} onToggle={toggle} onDelete={remove} />
          </div>
        )}
      </main>
      {compose && <PostComposer post={compose.post} photoIds={compose.photoIds} locations={locations} onClose={() => setCompose(null)} onSaved={saved} />}
    </div>
  );
}
